import { createSlice } from "@reduxjs/toolkit";

const STORAGE_KEY = "agavian_wishlist_items";

const isBrowser = () => typeof window !== "undefined";

const readStoredWishlist = () => {
  if (!isBrowser()) return [];

  try {
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (!storedValue) return [];

    const parsedValue = JSON.parse(storedValue);
    if (Array.isArray(parsedValue)) return parsedValue;
    if (parsedValue && typeof parsedValue === "object") {
      return Object.values(parsedValue);
    }
  } catch (error) {
    console.error("Unable to read wishlist", error);
  }

  return [];
};

const writeStoredWishlist = (itemsById) => {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.values(itemsById)));
  } catch (error) {
    console.error("Unable to save wishlist", error);
  }
};

const resolveProductId = (product = {}) =>
  product?.productId || product?.id || product?.product?.productId || null;

const normalizeWishlistItem = (product = {}) => {
  const nestedProduct = product?.product || {};
  const productId = resolveProductId(product);

  if (!productId) return null;

  const selectedImage =
    product?.selectedImageThumbKey ||
    product?.imageThumbNail ||
    product?.imageThumbnail ||
    product?.imageUrl ||
    nestedProduct?.selectedImageThumbKey ||
    nestedProduct?.imageThumbNail ||
    nestedProduct?.imageThumbnail ||
    nestedProduct?.imageUrl ||
    "";

  return {
    productId,
    name: product?.name || nestedProduct?.name || "Product",
    description: product?.description || nestedProduct?.description || "",
    categoryName: product?.categoryName || nestedProduct?.categoryName || "",
    price:
      product?.price ||
      product?.sellingPrice ||
      product?.distributionPrice ||
      nestedProduct?.price ||
      nestedProduct?.sellingPrice ||
      nestedProduct?.distributionPrice ||
      "",
    originalPrice: product?.originalPrice || product?.mrp || nestedProduct?.originalPrice || "",
    discount: product?.discount || product?.discountPercent || product?.discountPercentage || "",
    imageThumbNail: selectedImage,
    averageRating: product?.averageRating || nestedProduct?.averageRating || "",
    availableStock:
      product?.availableStock ?? product?.stockQuantity ?? nestedProduct?.availableStock ?? null,
    magicEnabled: Boolean(product?.magicEnabled || nestedProduct?.magicEnabled),
    colorEnabled: Boolean(product?.colorEnabled || nestedProduct?.colorEnabled),
    savedAt: product?.savedAt || new Date().toISOString(),
  };
};

const buildItemsById = (items = []) =>
  items.reduce((acc, item) => {
    const normalizedItem = normalizeWishlistItem(item);
    if (normalizedItem?.productId) {
      acc[String(normalizedItem.productId)] = normalizedItem;
    }
    return acc;
  }, {});

const initialState = {
  hydrated: false,
  itemsById: {},
};

const WishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    hydrateWishlist: (state) => {
      if (state.hydrated) return;
      state.itemsById = buildItemsById(readStoredWishlist());
      state.hydrated = true;
    },
    addToWishlist: (state, action) => {
      const wishlistItem = normalizeWishlistItem(action.payload);
      if (!wishlistItem?.productId) return;

      state.itemsById[String(wishlistItem.productId)] = wishlistItem;
      state.hydrated = true;
      writeStoredWishlist(state.itemsById);
    },
    removeFromWishlist: (state, action) => {
      const productId =
        typeof action.payload === "object" ? resolveProductId(action.payload) : action.payload;
      if (!productId) return;

      delete state.itemsById[String(productId)];
      state.hydrated = true;
      writeStoredWishlist(state.itemsById);
    },
    toggleWishlistProduct: (state, action) => {
      const wishlistItem = normalizeWishlistItem(action.payload);
      if (!wishlistItem?.productId) return;

      const key = String(wishlistItem.productId);
      if (state.itemsById[key]) {
        delete state.itemsById[key];
      } else {
        state.itemsById[key] = wishlistItem;
      }

      state.hydrated = true;
      writeStoredWishlist(state.itemsById);
    },
    clearWishlist: (state) => {
      state.itemsById = {};
      state.hydrated = true;
      writeStoredWishlist(state.itemsById);
    },
  },
});

export const {
  hydrateWishlist,
  addToWishlist,
  removeFromWishlist,
  toggleWishlistProduct,
  clearWishlist,
} = WishlistSlice.actions;

export default WishlistSlice.reducer;
